import styled from "@emotion/styled";
import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import querystring from "query-string";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import {
  addStripePaymentMethod,
  createStripeCustomer,
  startSubscription,
  updateStripeCustomer,
} from "../../../../api/Billing";
import { billingAtom } from "../../../../atoms/BillingAtom";
import { TestDriveBannerAtom } from "../../../../atoms/TestDriveBannerAtom";
import { MOBILE_WIDTH, TABLET_WIDTH } from "../../../../utils/HelperStyles";
import ActivateLeft from "./PaymentModal/ActivateLeft";
import CreditCardRight from "./PaymentModal/CreditCardRight";
import Summary from "./PaymentModal/Summary";

interface IProps {
  onOkClicked: () => void;
  onCancelClicked: () => void;
  isModal?: boolean;
}

const Wrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  position: relative;
  height: fit-content;
  margin-bottom: 50px;
`;

const ActivatePaymentContainer = styled.div<{ isModal?: boolean }>`
  /* display: flex; */
  /* flex-direction: column; */
  /* overflow-y: scroll; */
  margin-top: 50px;
  margin-right: 30px;

  height: ${(props) => (props.isModal ? "100%" : "600px")};
  letter-spacing: 0px;
  /* min-width: 1000px;

  @media (max-width: ${TABLET_WIDTH}) {
    min-width: 900px;
  }

  @media (max-width: 900px) {
    min-width: 700px;
  } */

  @media (max-width: ${MOBILE_WIDTH}) {
    flex-direction: column;
    align-items: center;
    overflow-y: scroll;
    height: 100vh;
    min-width: 100vw;
    margin-top: 20px;
  }
`;

export const HasFee = atom<boolean>({
  key: "HasFee",
  default: true,
});

export default function Payment(props: IProps): JSX.Element {
  useEffect(() => {
    const qs = querystring.parse(document.location.search) as { bid?: string };

    if (qs.bid) {
      localStorage.setItem("bid", qs.bid);
    }
  }, []);

  const { onOkClicked, onCancelClicked, isModal } = props;

  const resetBilling = useResetRecoilState(billingAtom);

  const stripe = useStripe();
  const elements = useElements();

  const hasFee = useRecoilValue(HasFee);

  const setTestDriveBanner = useSetRecoilState(TestDriveBannerAtom);

  const [paymentMethodId, setPaymentMethodId] = useState<string>();
  const [billing, setBilling] = useRecoilState(billingAtom);

  async function handleSubmit() {
    const valid = checkForm();

    if (!valid) {
      toast("Check your information", { type: "warning" });
      return false;
    }

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      toast("Unexpected error, please reload the page", { type: "error" });
      return false;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      return false;
    }

    let stripePaymentMethodId = paymentMethodId;
    // Use your card Element with other Stripe.js APIs
    if (!paymentMethodId) {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
      });

      if (!paymentMethod) {
        toast(error?.message || "", { type: "error" });
        return false;
      }

      if (error) {
        toast(error.message || "", { type: "error" });
        return false;
      } else {
        setPaymentMethodId(paymentMethod.id);
        stripePaymentMethodId = paymentMethod.id;
      }
    }

    let stripeCustomerId = billing.customerId;

    if (!billing.customerId) {
      const customerIDResponse = await createStripeCustomer({
        name: billing.name as string,
        billing_address: { ...billing.billingAddress },
        shipping_address: { ...billing.shippingAddress },
        vatId: billing.vatId,
        companyName: billing.companyName,
      });

      if (!customerIDResponse.success) {
        toast(customerIDResponse.message, { type: "error" });
        return false;
      }

      setBilling((prevState) => ({
        ...prevState,
        customerId: customerIDResponse.id,
      }));

      setTestDriveBanner({
        show: false,
        testDriveExpiration: 0,
      });

      stripeCustomerId = customerIDResponse.id;
    } else {
      const customerIDResponse = await updateStripeCustomer({
        billing_address: { ...billing.billingAddress },
        name: billing.name as string,
        shipping_address: { ...billing.shippingAddress },
        vatId: billing.vatId,
        companyName: billing.companyName,
      });

      if (!customerIDResponse.success) {
        toast(customerIDResponse.message, { type: "error" });
        return false;
      }

      setBilling((prevState) => ({
        ...prevState,
        customerId: customerIDResponse.id,
      }));
      stripeCustomerId = customerIDResponse.id;
    }

    if (!stripePaymentMethodId) return false;

    const addPaymentResponse = await addStripePaymentMethod({
      paymentMethodId: stripePaymentMethodId,
    });

    if (!addPaymentResponse.success) {
      toast(addPaymentResponse.message, { type: "error" });
      setPaymentMethodId(undefined);
      return false;
    }

    try {
      await startSubscription({
        externalUsers: billing.externalUsers,
        isUpdate: billing.activeUser && !!billing.subscriptionId,
        admins: billing.admins,
        tier: billing.tier,
        hasFee,
      });
      if (billing.activeUser && billing.subscriptionId) {
        onCancelClicked();
      } else {
        // setUpsells([
        //   {
        //     description: "SocialLair Subscription",
        //     needShipping: false,
        //     price: ((billing.basePrice || 0) + billing.additionalSum) * 100,
        //     uid: "SocialLairSubscription",
        //   },
        // ]);
        onOkClicked();
        return true;
      }
    } catch (err) {
      toast(err.message, { type: "error" });
    }

    resetBilling();
    return false;
  }

  function checkBilling() {
    let valid = true;

    Object.entries(billing.billingAddress).map((item) => {
      const [key, value] = item;
      if (!value) {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            billingAddress: {
              ...prevState.errors.billingAddress,
              [key]: true,
            },
          },
        }));
        valid = false;
      } else {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            billingAddress: {
              ...prevState.errors.billingAddress,
              [key]: false,
            },
          },
        }));
      }
    });
    return valid;
  }

  function checkShipping() {
    let valid = true;

    Object.entries(billing.shippingAddress).map((item) => {
      const [key, value] = item;
      if (!value) {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            shippingAddress: {
              ...prevState.errors.shippingAddress,
              [key]: true,
            },
          },
        }));
        valid = false;
      } else {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            shippingAddress: {
              ...prevState.errors.shippingAddress,
              [key]: false,
            },
          },
        }));
      }
    });
    return valid;
  }

  function checkForm() {
    // Check billing address
    let valid = true;
    let newErrors = { ...billing.errors };

    valid = checkBilling();

    if (billing.customShipping) {
      valid = checkShipping() && valid;
    }

    if (billing.name === "" || !billing.name) {
      valid = false;
      newErrors.name = true;
    } else {
      newErrors.name = false;
    }

    if (!billing.termsAndConditions) {
      valid = false;
    }

    setBilling((prevState) => ({
      ...prevState,
      errors: newErrors,
    }));

    return valid;
  }

  return (
    <Wrapper>
      <ActivatePaymentContainer isModal={isModal}>
        <ActivateLeft />
        <CreditCardRight />
      </ActivatePaymentContainer>
      <Summary handleSubmit={handleSubmit} />
    </Wrapper>
  );
}
